import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Users, Briefcase, Megaphone, FileText, ClipboardText, SignOut, House,
  Globe, Airplane, ChartBar, Buildings, Shield,
  EnvelopeSimple, Newspaper, Wrench, Quotes, HandHeart, ChatCircle, Package, Info, FilePdf,
  Archive, List, GraduationCap, Trophy, Database, HardDrive, Sparkle
} from '@phosphor-icons/react';
import { COLORED_LOGO } from '../components/admin/adminConstants';
import { UserManagement } from '../components/admin/UserManagement';
import { DepartmentManagement } from '../components/admin/DepartmentManagement';
import { PermissionsPanel } from '../components/admin/PermissionsPanel';
import { JobManagement } from '../components/admin/JobManagement';
import { ApplicationsView } from '../components/admin/ApplicationsView';
import { JobArchiveView } from '../components/admin/JobArchiveView';
import { HolidayAdmin } from '../components/admin/HolidayAdmin';
import { AnnouncementManagement } from '../components/admin/AnnouncementManagement';
import { DocumentManagement } from '../components/admin/DocumentManagement';
import { RequestManagement } from '../components/admin/RequestManagement';
import { NewsManagement } from '../components/admin/NewsManagement';
import { CommunityManagement } from '../components/admin/CommunityManagement';
import { CaseStudiesManagement } from '../components/admin/CaseStudiesManagement';
import { DataExportImport } from '../components/admin/DataExportImport';
import { Backups } from '../components/admin/Backups';
import { TestimonialManagement } from '../components/admin/TestimonialManagement';
import { QueryCategoryManagement } from '../components/admin/QueryCategoryManagement';
import { ServicesAdminManagement } from '../components/admin/ServicesAdminManagement';
import { AboutAdminManagement } from '../components/admin/AboutAdminManagement';
import { CareersAdminManagement } from '../components/admin/CareersAdminManagement';
import { FooterDocumentManagement } from '../components/admin/FooterDocumentManagement';
import { FooterPartnersManagement } from '../components/admin/FooterPartnersManagement';
import { SiteSettings } from '../components/admin/SiteSettings';
import { useSiteLogo } from '../hooks/useSiteLogo';
import { ServiceTypeManagement } from '../components/admin/ServiceTypeManagement';
import { DriverTrainingCoursesManagement } from '../components/admin/DriverTrainingCoursesManagement';
import { WebsiteCMS } from '../components/admin/WebsiteCMS';

export default function AdminDashboard() {
  const { user, logout, hasPermission, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const headerLogo = useSiteLogo();

  useEffect(() => { if (!loading && !user) navigate('/login'); }, [user, navigate, loading]);
  const handleLogout = () => { logout(); navigate('/'); };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent"></div></div>;
  if (!user) return null;

  const tabs = [
    { id: 'users', label: 'Users', icon: Users, perm: 'users' },
    { id: 'departments', label: 'Departments', icon: Buildings, perm: 'departments' },
    { id: 'permissions', label: 'Permissions', icon: Shield, perm: 'permissions' },
    { id: 'jobs', label: 'Job Listings', icon: Briefcase, perm: 'jobs' },
    { id: 'applications', label: 'Applications', icon: EnvelopeSimple, perm: 'applications' },
    { id: 'job-archive', label: 'Job Archive', icon: Archive, perm: 'jobs' },
    { id: 'news', label: 'News Stories', icon: Newspaper, perm: 'website' },
    { id: 'community', label: 'Community', icon: HandHeart, perm: 'website' },
    { id: 'case-studies', label: 'Case Studies', icon: Trophy, perm: 'website' },
    { id: 'testimonials', label: 'Testimonials', icon: Quotes, perm: 'website' },
    { id: 'services-admin', label: 'Services', icon: Package, perm: 'services' },
    { id: 'about-admin', label: 'About Page', icon: Info, perm: 'about' },
    { id: 'careers-admin', label: 'Careers Page', icon: GraduationCap, perm: 'website' },
    { id: 'service-types', label: 'Service Types', icon: Wrench, perm: 'website' },
    { id: 'driver-courses', label: 'Driver Courses', icon: GraduationCap, perm: 'website' },
    { id: 'query-categories', label: 'Query Categories', icon: ChatCircle, perm: 'website' },
    { id: 'footer-docs', label: 'Footer Documents', icon: FilePdf, perm: 'footer_docs' },
    { id: 'footer-partners', label: 'Footer Partners', icon: Trophy, perm: 'footer_docs' },
    { id: 'site-settings', label: 'Site Settings', icon: Sparkle, perm: 'website' },
    { id: 'holidays', label: 'Holiday Admin', icon: Airplane, perm: 'holidays' },
    { id: 'announcements', label: 'Announcements', icon: Megaphone, perm: 'announcements' },
    { id: 'documents', label: 'HR Documents', icon: FileText, perm: 'documents' },
    { id: 'requests', label: 'Requests', icon: ClipboardText, perm: 'requests' },
    { id: 'website', label: 'Website CMS', icon: Globe, perm: 'website' },
    { id: 'data', label: 'Data Export/Import', icon: Database, perm: 'permissions' },
    { id: 'backups', label: 'Backups', icon: HardDrive, perm: 'permissions' },
  ].filter(t => hasPermission(t.perm));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-30">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="admin-mobile-menu"><List size={22} className="text-slate-700" /></button>
        <img src={headerLogo} alt="NSEC" className="h-8" />
        <div className="w-8 h-8 rounded-full bg-lime-600 text-white flex items-center justify-center text-xs font-bold">{user?.first_name?.[0]}{user?.last_name?.[0]}</div>
      </div>
      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed left-0 top-0 bottom-0 w-56 bg-white border-r border-slate-200 flex flex-col z-40 transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Link to="/" className="p-5 pb-3"><img src={headerLogo} alt="NSEC" className="h-10" /><p className="text-[11px] text-slate-400 mt-1 tracking-wide">Administration</p></Link>
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-sm ${activeTab === tab.id ? 'bg-lime-600 text-white font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-testid={`admin-tab-${tab.id}`}>
              <tab.icon size={17} weight={activeTab === tab.id ? 'fill' : 'regular'} /><span>{tab.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <Link to="/analytics" className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-slate-500 hover:bg-slate-50 text-sm" data-testid="admin-analytics-link"><ChartBar size={17} /><span>Analytics</span></Link>
          <Link to="/intranet" className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-slate-500 hover:bg-slate-50 text-sm"><House size={17} /><span>Intranet</span></Link>
          <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 text-sm" data-testid="admin-logout"><SignOut size={17} /><span>Sign Out</span></button>
        </div>
      </aside>
      <main className="lg:ml-56 p-4 sm:p-6 lg:p-8 pt-18 lg:pt-8"><div className="max-w-[1400px] mx-auto">
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'departments' && <DepartmentManagement />}
        {activeTab === 'permissions' && <PermissionsPanel />}
        {activeTab === 'jobs' && <JobManagement />}
        {activeTab === 'applications' && <ApplicationsView />}
        {activeTab === 'job-archive' && <JobArchiveView />}
        {activeTab === 'news' && <NewsManagement />}
        {activeTab === 'community' && <CommunityManagement />}
        {activeTab === 'case-studies' && <CaseStudiesManagement />}
        {activeTab === 'data' && <DataExportImport />}
        {activeTab === 'backups' && <Backups />}
        {activeTab === 'testimonials' && <TestimonialManagement />}
        {activeTab === 'services-admin' && <ServicesAdminManagement />}
        {activeTab === 'about-admin' && <AboutAdminManagement />}
        {activeTab === 'careers-admin' && <CareersAdminManagement />}
        {activeTab === 'service-types' && <ServiceTypeManagement />}
        {activeTab === 'driver-courses' && <DriverTrainingCoursesManagement />}
        {activeTab === 'query-categories' && <QueryCategoryManagement />}
        {activeTab === 'footer-docs' && <FooterDocumentManagement />}
        {activeTab === 'footer-partners' && <FooterPartnersManagement />}
        {activeTab === 'site-settings' && <SiteSettings />}
        {activeTab === 'holidays' && <HolidayAdmin />}
        {activeTab === 'announcements' && <AnnouncementManagement />}
        {activeTab === 'documents' && <DocumentManagement />}
        {activeTab === 'requests' && <RequestManagement />}
        {activeTab === 'website' && <WebsiteCMS />}
      </div></main>
    </div>
  );
}
